import Cookies from "js-cookie";
import tippy from "tippy.js";
import * as QueryString from "query-string";
import {
  callAccommodationApi,
  setQueryString,
  setSearchLoading,
} from "./simple-search";
import {
  queryStringToRecord,
  formDataToQueryString,
  appendToFormData,
  generateSearchConfig,
} from "../util/helpers";

let mediaRecorder: any;
let audioChunks: any = [];
let recordingActive: boolean = false;
const aiSearchSelector = ".ai-search";

declare global {
  interface Window {
    toggleAiSearch: CallableFunction;
  }
}
declare var aiMaxRecordingLength: number;

function setup() {
  const aiSearchForm = document.querySelector(aiSearchSelector);
  if (!aiSearchForm) {
    return;
  }
  instantiateAiSearchToggle();
  if (!aiSearchForm.hasAttribute("data-quick-search")) {
    aiSearchForm.addEventListener("submit", (evt: any) => {
      evt.preventDefault();

      aiSearchForm.querySelector<HTMLInputElement>(
        "input[name='showpage']",
      ).value = "";
      triggerNewAiSearch(evt);
      document.dispatchEvent(
        new Event("gloversure.accommodation.search.first-load"),
      );
    });
  }
  tippy(".microphone-inactive", {
    content: `Recording can be a maximum of ${aiMaxRecordingLength}s`,
    trigger: "click",
  });
  document
    .querySelector("#toggle-recording")
    .addEventListener("click", toggleRecording);

  document.addEventListener(
    "gloversure.accommodation.ai-search.trigger",
    (evt) => {
      const useAiSearch =
        Cookies.get("ai-search") !== undefined
          ? Cookies.get("ai-search") == "true"
          : true;
      if (!useAiSearch) {
        return;
      }
      triggerNewAiSearchFromForm(
        document.querySelector<HTMLFormElement>("form.ai-search"),
      );
    },
  );
}

function simpleSetup() {
  instantiateAiSearchToggle();
  document
    .getElementById("toggle-recording")
    .addEventListener("click", toggleRecording);
}

export function aiSearchEnabled() {
  const useAiSearch = Cookies.get("ai-search");
  if (useAiSearch != undefined) {
    return useAiSearch === "true";
  }
  const aiSearchForm = document.querySelector("[data-ai-search]")
  if (!aiSearchForm) return false
  return (aiSearchForm as HTMLInputElement).checked
}

function instantiateAiSearchToggle() {
  const aiSearchToggle = document.querySelector<HTMLInputElement>(
    ".ai-search-toggle [data-ai-search]",
  );

  aiSearchToggle.addEventListener("click", toggleAiSearch);
  document
    .querySelectorAll<HTMLElement>(".ai-toggle-description")
    .forEach((elem) => {
      elem.addEventListener("click", () => {
        aiSearchToggle.checked = !aiSearchToggle.checked;
        toggleAiSearch();
      });
    });
  window.toggleAiSearch = toggleAiSearch;
}

function toggleAiSearch() {
  document
    .querySelectorAll(
      aiSearchSelector + ", .ai-suggestions, #supercontrol-search-form, #quick-search",
    )
    .forEach((elem) => elem.classList.toggle("toggle-hidden"));

  const toggleElem = document.querySelector<HTMLElement>("[data-ai-search]");

  const aiSearchEnabled =
    Cookies.get("ai-search") !== undefined
      ? Cookies.get("ai-search")
      : toggleElem.dataset.aiSearch;
  const aiSearchValue = (aiSearchEnabled != "true").toString();

  Cookies.set("ai-search", aiSearchValue, { expires: 365 });
  toggleElem.dataset.aiSearch = aiSearchValue;
}

function newVoiceAiSearch(audioBlob: any, isQuickSearch: boolean = false) {
  const formData = new FormData();
  formData.append("audio", audioBlob, "voice-recording.webm");
  if (!isQuickSearch) {
    setSearchLoading();
  }
  callAccommodationApi(
    `/wp-json/accommodation/uploadaiaudio${isQuickSearch ? "?quicksearch=true" : ""}`,
    formData,
    (result: any) => {
      if (!("ai-query" in result)) {
        return;
      }
      (
        document.querySelector(
          aiSearchSelector + " input[name=q]",
        ) as HTMLInputElement
      ).value = result["ai-query"];
      if (!isQuickSearch) {
        setQueryString({ q: result["ai-query"] });
        return;
      }
      (document.querySelector(aiSearchSelector) as HTMLFormElement).submit();
    },
    !isQuickSearch,
  );
}

function toggleActiveMicrophone() {
  const elems = document.querySelectorAll(
    "#toggle-recording .microphone-active, #toggle-recording .microphone-inactive, #ai-search-button .microphone-active, #ai-search-button .microphone-inactive",
  );
  elems.forEach((elem) => elem.classList.toggle("toggle-hidden"));
  const searchButton = document.getElementById("ai-search-button") as HTMLButtonElement;
  if (searchButton) {
    searchButton.disabled = !searchButton.disabled;
  }
}

function toggleRecording(evt: any) {
  const isQuickSearch =
    "quickSearch" in evt.target.closest(aiSearchSelector).dataset;
  if (recordingActive) {
    recordingEnd();
  } else {
    recordingStart(isQuickSearch);
  }
  toggleActiveMicrophone();
  recordingActive = !recordingActive;
}

function recordingEnd() {
  mediaRecorder.stop();
}

function recordingStart(isQuickSearch: boolean = false) {
  navigator.mediaDevices
    .getUserMedia({
      audio: true,
    })
    .then((stream) => {
      // @ts-ignore
      mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.start();
      const recordingStartTime = Date.now();
      mediaRecorder.addEventListener("dataavailable", (event: any) => {
        if ((Date.now() - recordingStartTime) / 1000 > aiMaxRecordingLength) {
          //toggle recording off if it exceeds 15s
          return;
        }
        audioChunks.push(event.data);
      });
      mediaRecorder.addEventListener("stop", () => {
        const audioBlob = new Blob(audioChunks);
        newVoiceAiSearch(audioBlob, isQuickSearch);
        audioChunks = [];
      });
    })
    .catch(() => {
      toggleActiveMicrophone();
    });
}

function triggerNewAiSearch(evt: any) {
  evt.preventDefault();
  triggerNewAiSearchFromForm(evt.target);
}

function triggerNewAiSearchFromForm(form: HTMLFormElement) {
  const formData = new FormData(form);
  newAiSearch(formData);
}

function fetchAiFormData(): FormData {
  const formData = new FormData(document.querySelector(aiSearchSelector));

  //get the allowed keys from the query string and add to form form data
  const queryString = QueryString.parse(window.location.search);
  ["q", "showpage", "sort"].forEach((key: string) => {
    if (Object.keys(queryString).includes(key)) {
      formData.set(key, queryString[key].toString());
    }
  });
  return formData;
}

export function newAiSearch(formData: FormData) {
  //if no form data is provided go and find it
  if ([...formData].length == 0 || !formData) {
    formData = fetchAiFormData();
  }

  formData = appendToFormData(formData, generateSearchConfig());

  //convert to query string
  const queryString = formDataToQueryString(formData);

  //do all the search bits
  setQueryString(queryStringToRecord(queryString));
  setSearchLoading();
  callAccommodationApi(
    `/wp-json/accommodation/aisearch?${queryString.toString()}`,
    null,
  );
}

export default { setup, simpleSetup };
